import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import React, { useState } from 'react';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./Checkbox.module.scss');

export interface Props {
  autoSaveUrl?: string;
  checked?: boolean;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  indeterminate?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string | JSX.Element;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, name?: string, value?: boolean) => void;
  required?: boolean;
  context?: 'talent' | 'company' | 'neutral';
  value?: string;
}

const Checkbox: React.FC<Props> = ({
  autoSaveUrl,
  checked,
  children,
  className,
  disabled,
  error,
  id,
  indeterminate,
  inputRef,
  label,
  name,
  onChange,
  required,
  context = 'neutral',
  value,
}) => {
  const [resolvedId] = useState(() => id || `checkbox-${Math.random().toString(36).slice(2, 9)}`);
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: name || '', debounceMs: 0 });
  const isControlled = onChange !== undefined;
  const [internalChecked, setInternalChecked] = useState(checked ?? false);

  const resolvedChecked = isControlled ? checked : internalChecked;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    if (!isControlled) {
      setInternalChecked(newValue);
    }
    if (autoSaveUrl) save(newValue ? '1' : '0');
    if (onChange) onChange(event, name, newValue);
  };

  const checkboxStyles = classNames(styles.checkbox, styles[context], {
    [styles.indeterminate]: indeterminate,
    [styles.selected]: resolvedChecked && !indeterminate,
    [styles.disabled]: disabled,
  });

  const errorId = error ? `${resolvedId}-error` : undefined;

  return (
    <div className={classNames(styles.wrapper, className)}>
      <label className={styles.container} htmlFor={resolvedId}>
        {name && <input type="hidden" name={name} value="0" />}
        <input
          aria-describedby={errorId}
          aria-invalid={Boolean(error)}
          checked={isControlled ? resolvedChecked : undefined}
          className={styles.input}
          defaultChecked={!isControlled ? checked : undefined}
          disabled={disabled}
          id={resolvedId}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          required={required}
          type="checkbox"
          value={value || '1'}
        />
        <span className={checkboxStyles} aria-hidden="true">
          <Icon name={indeterminate ? 'minus' : 'check'} color="white" size="sm" weight="bold" />
        </span>
        {label && (
          <span className={classNames('checkboxLabel', styles.label)}>
            {typeof label === 'string'
              ? <span dangerouslySetInnerHTML={{ __html: label }} />
              : label}
          </span>
        )}
      </label>
      {children && <div className={styles.childrenContainer}>{children}</div>}
      {error && !resolvedChecked && (
        <div className={styles.errorContainer} id={errorId} role="alert">
          <Icon name="triangleAlert" color="error" size="sm" aria-hidden="true" />
          <p className={styles.errorText}>{error}</p>
        </div>
      )}
    </div>
  );
};

export default Checkbox;
